import {
  setSuperProperties,
  unsetSuperProperties
} from "@asosteam/asos-web-event-tracking-bridge";
import { getPageAnalytics } from "@src/client/analytics/getPageAnalytics";
import {
  asosGlobalNamespace,
  siteChromeGlobalNamespace
} from "@template/constants";
import { noticeError } from "@src/helpers/monitoring";
import { setupPageDataEvent } from "./setupPageDataEvent";

/**
 * Updates page-scoped analytics super properties and re-fires pageData event.
 * Called on both initial page load and client-side navigation events.
 *
 * This function:
 * 1. Reads analytics data from DOM (getPageAnalytics - currently supports Homepage and Floor Page only)
 *    and falls back to namespace (mixpanelSuperProperties) for other page types
 * 2. Unsets previous page_type and page_id super properties
 * 3. Sets new page-scoped super properties if available
 * 4. Re-fires the pageData event so it reflects the current URL/title (referrer may remain the initial referrer on SPA navigation)
 *
 * Note: For most client-side navigations (e.g., PLP→PLP, Search→PLP), hosts must update
 * `mixpanelSuperProperties` in the window namespace, as DOM-based extraction is limited.
 *
 * @returns {Promise<boolean>}
 */
export const updatePageScopedAnalytics = async () => {
  const domAnalyticsData = getPageAnalytics();

  const mixpanelSuperProperties =
    window[asosGlobalNamespace]?.[siteChromeGlobalNamespace]
      ?.mixpanelSuperProperties || {};

  const { analyticsPageType, analyticsPageId } = {
    ...mixpanelSuperProperties,
    ...domAnalyticsData
  };

  try {
    await unsetSuperProperties(["page_type", "page_id"], {
      persistent: false
    });
  } catch (error) {
    noticeError(error);
  }

  const pageScopedProperties = {
    ...(analyticsPageType &&
      analyticsPageType !== "<!--%analyticsPageType%-->" && {
        page_type: analyticsPageType
      }),
    ...(analyticsPageId &&
      analyticsPageId !== "<!--%analyticsPageId%-->" && {
        page_id: analyticsPageId
      })
  };

  if (Object.keys(pageScopedProperties).length > 0) {
    try {
      await setSuperProperties(pageScopedProperties, { persistent: false });
    } catch (error) {
      noticeError(error);
    }
  }

  const pushToDataLayer =
    window[asosGlobalNamespace]?.[siteChromeGlobalNamespace]?.pushToDataLayer;

  const canFirePageData =
    typeof pushToDataLayer === "function" &&
    window[asosGlobalNamespace]?.webContext;

  let pageDataFired = false;

  if (canFirePageData) {
    try {
      await setupPageDataEvent();
      pageDataFired = true;
    } catch (error) {
      noticeError(error);
    }
  }

  return pageDataFired;
};
